/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { isError } from "@epic-mw/error-types";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "@epic-mw/app-context";
import { useLocale } from "@epic-mw/i18n";
import { useCosmosComponentContext } from "@components/context";
import { buildHygraphCompressedImageUrl } from "@components/utils";

/**
 * Wraps `<blockquote>` content in double-quotes if not already quoted.
 * Accepts both straight (`"`) and curly (`"` / `"`) quotes as existing delimiters.
 *
 * @param html - HTML string potentially containing `<blockquote>` elements.
 * @returns The processed HTML string, or `""` if input is falsy.
 */
export const ensureQuotesInBlockquotes = (html?: string | null): string => {
	return (
		html?.toString().replace(/<blockquote>([\s\S]*?)<\/blockquote>/g, (_match, content: string) => {
			let trimmed = content?.trim() || "";
			if (!(trimmed.startsWith('"') || trimmed.startsWith("\u201C"))) trimmed = `"${trimmed}`;
			if (!(trimmed.endsWith('"') || trimmed.endsWith("\u201D"))) trimmed = `${trimmed}"`;
			return `<blockquote>${trimmed}</blockquote>`;
		}) || ""
	);
};

/**
 * Appends a `lang` query parameter to relative `href` values in an HTML string,
 * unless the locale is already the first path segment or `lang` is already set.
 * Skipped entirely in locale-mode "never" — those sites don't use query-param locale.
 *
 * @param locale - The locale to append (e.g. `"en"`).
 * @param html - HTML string containing `href` attributes to localize.
 * @returns The HTML string with localized hrefs, or `""` if input is falsy.
 */
export const localizeLinks = (locale: string, html?: string | null): string => {
	// In "never" mode, URLs are locale-free — don't append ?lang
	if (process.env.NEXT_PUBLIC_LOCALE_MODE === "never") {
		return html || "";
	}

	return (
		// catch any href values, contained in double or single quote
		html?.replace(/href=(["'])(\/[^"']+)\1/g, (match, quote, href: string) => {
			try {
				const tempBase = "https://base";
				const url = new URL(href, tempBase);

				// don't append lang param if locale path is already present
				const pathSegments = url.pathname.split("/").filter(Boolean);
				if (pathSegments.length > 0 && pathSegments[0] === locale) {
					return match;
				}

				// append lang param if not already present
				if (!url.searchParams.has("lang")) {
					url.searchParams.append("lang", locale);
				}
				const updatedHref = url.href.replace(tempBase, "");
				return `href=${quote}${updatedHref}${quote}`;
			} catch (error) {
				console.error("CosmosRichText - Error localizing relative href: ", href, error);
				return match;
			}
		}) || ""
	);
};

const ytDomainRegex = new RegExp('src="(http:|https:)?(//)?(www.)?youtube.com/embed/', "ig");
const ytNoCookieReplacement = 'src="https://www.youtube-nocookie.com/embed/';

/**
 * Replaces `youtube.com/embed/` src URLs with the privacy-friendly `youtube-nocookie.com` domain.
 *
 * @param copy - HTML string potentially containing YouTube embed iframes.
 * @returns The HTML string with YouTube embed domains replaced.
 */
export const youtubeEmbedSanitize = (copy: string): string => {
	return copy.replace(ytDomainRegex, ytNoCookieReplacement);
};

/**
 * Ensures Vimeo embed `src` URLs include the `dnt=true` query parameter.
 *
 * @param copy - HTML string potentially containing Vimeo embed iframes.
 * @returns The HTML string with `dnt=true` appended to all Vimeo player URLs.
 */
export const vimeoEmbedSanitize = (copy: string): string => {
	let sanitized = copy || "";

	if (sanitized.indexOf("player.vimeo.com") > -1) {
		const vimeoDomainRegex = new RegExp('src="((http:|https:)?//player.vimeo.com/video/[a-z0-9?=&;_]+)"', "ig");

		let match = vimeoDomainRegex.exec(sanitized);
		while (match !== null) {
			if (match && match.length && match[1]) {
				const originalVimeoSrc = match[1].replace(/&amp;/gi, "&");
				try {
					const parsedUrl = new URL(originalVimeoSrc);
					if (parsedUrl && parsedUrl.searchParams.get("dnt") !== "true") {
						parsedUrl.searchParams.set("dnt", "true");
						sanitized = sanitized.replace(match[1], parsedUrl.toString());
						sanitized = sanitized.replace(/&amp%3B/gi, "&amp;");
					}
				} catch (e) {
					console.error("Failed to create URL from vimeosrc", isError(e) && e.message);
				}
			}
			match = vimeoDomainRegex.exec(sanitized);
		}
	}
	return sanitized;
};

/**
 * Replaces non-breaking hyphens (U+2011) with regular hyphens (U+002D).
 * Required because CMS content sometimes contains Unicode 8209 which does not
 * render correctly on all platforms (e.g. macOS).
 *
 * @param text - String potentially containing Unicode non-breaking hyphens.
 * @returns The string with all U+2011 characters replaced by `-`.
 */
export const replaceUnicodeDash = (text: string): string => {
	let finalText = text;
	if (finalText && finalText.indexOf("\u2011") !== -1) {
		// replace char code 8209 with 45
		finalText = finalText.replace(/\u2011/g, "-");
	}
	return finalText;
};

/**
 * Replaces `/:locale` and `/{locale}` path placeholders with the given locale.
 *
 * @param locale - The locale to substitute (e.g. `"en"`).
 * @param copy - HTML string potentially containing locale placeholders.
 * @returns The HTML string with locale placeholders replaced.
 */
export const replaceDynamicLocale = (locale: string, copy: string): string => {
	let finalCopy = copy;
	if (finalCopy && finalCopy.indexOf("/:locale") > -1) {
		finalCopy = finalCopy.replace(/\/:locale/gi, `/${locale}`);
	}
	if (finalCopy && finalCopy.indexOf("/{locale}") > -1) {
		finalCopy = finalCopy.replace(/\/\{locale\}/gi, `/${locale}`);
	}
	return finalCopy;
};

/**
 * Regex replacer callback for {@link sanitizeTextAlign}.
 * Maps `left` → `text-align:start` and `right` → `text-align:end`;
 * all other values pass through unchanged.
 *
 * @param _ - Full regex match (unused).
 * @param dir - The captured alignment direction (e.g. `"left"`, `"right"`).
 * @returns The logical `text-align` CSS declaration.
 */
export const replaceTextAlign = (_: string, dir: string): string => {
	const cleanDir = dir.toLowerCase();
	if (cleanDir === "left") return "text-align:start";
	if (cleanDir === "right") return "text-align:end";
	return `text-align:${dir}`;
};

/**
 * Replaces directional CSS `text-align` values with their logical equivalents to
 * support RTL/LTR layouts correctly.
 *
 * - `text-align: left`  → `text-align:start`
 * - `text-align: right` → `text-align:end`
 *
 * This is necessary because CMS-generated HTML often contains physical alignment
 * values that break mirrored layouts in RTL locales (e.g. Arabic, Hebrew).
 *
 * @param html - HTML string potentially containing inline `text-align` styles.
 * @returns The HTML string with directional text-align values replaced by logical values,
 *          or the original string unchanged if no replacements were needed.
 */
export const sanitizeTextAlign = (html: string): string => {
	if (!html || !html.toLowerCase().includes("text-align:")) return html;

	return html.replace(/text-align:\s*(left|right)/gi, replaceTextAlign);
};

/** Remove empty paragraphs commonly left by CKEditor / CMS content */
const removeEmptyParagraphs = (html: string): string => {
	return html.replace(/<p[^>]*>\s*(&nbsp;|&#160;)?\s*<\/p>/gi, "");
};

/**
 * Rewrites Hygraph asset `<img>` srcs in CKEditor HTML to compressed variants
 * (`quality=value:90/auto_image`). CKEditor embeds the raw asset URL — often
 * with a `resize=` segment from the editor's resize handles but no compression —
 * which otherwise serves the original format at full quality. Any existing
 * geometry transform (e.g. `resize=height:80`) is preserved and compression is
 * inserted before it; no `resize=` width variant is added, since the rendered
 * width is unknown here.
 *
 * URLs that already carry compression/format transforms and non-Hygraph srcs
 * pass through unchanged (see `buildHygraphCompressedImageUrl`). SVG/GIF assets
 * can't be detected from the URL (Hygraph asset URLs have no extension), so they
 * are transformed like any other image.
 *
 * @param hygraphMediaDomain - The configured Hygraph media CDN domain. Built-in
 *        Hygraph host patterns are matched even when this is empty.
 * @param html - HTML string potentially containing `<img>` elements.
 * @returns The HTML string with Hygraph image srcs rewritten.
 */
export const optimizeHygraphImages = (hygraphMediaDomain: string, html: string): string => {
	if (!html || !/<img/i.test(html)) return html;

	// [^<>] keeps the per-tag scan from running past an unterminated tag to the
	// end of the document (quadratic on adversarial input); a literal `<` or `>`
	// inside an attribute value may make the tag pass through unmodified instead.
	return html.replace(/<img\b[^<>]*>/gi, (imgTag) =>
		// the leading \s boundary keeps `data-src=` (lazy-load markup) from matching
		imgTag.replace(/(\s)src=(["'])(.*?)\2/i, (match, space: string, quote: string, src: string) => {
			const optimizedSrc = buildHygraphCompressedImageUrl(hygraphMediaDomain, src);
			if (!optimizedSrc) return match;
			return `${space}src=${quote}${optimizedSrc}${quote}`;
		}),
	);
};

/**
 * Runs the full CMS-HTML sanitization pipeline (see the individual helpers).
 *
 * @param locale - Locale used for link localization and placeholder substitution.
 * @param _location - Unused placeholder retained for call-site compatibility.
 * @param html - Raw CMS HTML.
 * @param hygraphMediaDomain - When provided (including `""`, which still matches
 *        the built-in Hygraph host patterns), Hygraph `<img>` srcs are rewritten
 *        to compressed variants via {@link optimizeHygraphImages}; when omitted,
 *        image optimization is skipped entirely.
 * @returns The sanitized HTML string, or `""` if input is falsy.
 */
export const formatHtml = (
	locale: string,
	_location: URL,
	html?: string | null,
	hygraphMediaDomain?: string,
): string => {
	if (!html) return "";

	html = removeEmptyParagraphs(html);
	html = youtubeEmbedSanitize(html);
	html = vimeoEmbedSanitize(html);
	html = replaceUnicodeDash(html);
	html = replaceDynamicLocale(locale, html);

	if (hygraphMediaDomain !== undefined) {
		html = optimizeHygraphImages(hygraphMediaDomain, html);
	}

	html = sanitizeTextAlign(html);
	html = ensureQuotesInBlockquotes(html);

	if (locale) {
		html = localizeLinks(locale, html);
	}

	return html;
};

/**
 * Last-resort link neutralisation for game mode, used only when the
 * `CosmosRichText.game` chunk fails to load.
 *
 * Deliberately unconditional: it neutralises *every* anchor instead of honouring
 * the allowlist `cleanForGame` applies. Reproducing that allowlist here would
 * mean pulling it, and the URL parsing it needs, into the main bundle — which is
 * what the dynamic import exists to avoid. In a webview with no nav a live link
 * strands the user, so this path errs restrictive and drops the allowlisted
 * links too.
 *
 * Client-only: it runs from a `useEffect`, so native `DOMParser` is available
 * and the xmldom server path `cleanForGame` carries isn't needed.
 *
 * @param html - Already-formatted article HTML.
 * @returns The same HTML with every `<a>` replaced by non-clickable text, or
 *          `""` if even the fallback fails (never live links).
 */
export const stripAnchors = (html: string): string => {
	try {
		const dom = new DOMParser().parseFromString(html, "text/html");
		// Snapshot first — getElementsByTagName is live, and we mutate as we go.
		for (const anchor of Array.from(dom.getElementsByTagName("a"))) {
			const href = anchor.getAttribute("href");
			const text = anchor.textContent ?? "";
			const span = dom.createElement("span");
			// Mirror cleanForGame's treatment of a non-allowlisted link: the text,
			// then the destination in brackets so it stays readable, dimmed.
			span.textContent = href ? `${text} [${href}]` : text;
			span.setAttribute("style", "opacity: 0.65;");
			// replaceChild, not insertBefore + display:none, so no hidden anchor
			// survives for a stylesheet to bring back.
			anchor.parentNode?.replaceChild(span, anchor);
		}
		return dom.body.innerHTML;
	} catch (err) {
		console.error("Game-mode link fallback failed; dropping HTML rather than shipping live links", err);
		return "";
	}
};

/**
 * React hook that applies {@link formatHtml} to the given HTML, memoized on
 * locale, location, html, the context's `hygraphMediaDomain`, and options
 * changes. Hygraph image optimization is always enabled here — the domain
 * comes from {@link useCosmosComponentContext}.
 *
 * @param html - Raw HTML string to format.
 * @param options - `gameMode` additionally runs the async in-game HTML cleanup
 *        (`cleanForGame`), loaded on demand.
 * @returns Memoized sanitized HTML string.
 */
export const useFormatHtml = (html?: string | null, options?: { gameMode?: boolean }): string => {
	const location = useLocation();
	const locale = useLocale();
	const { hygraphMediaDomain } = useCosmosComponentContext();

	const baseHtml = useMemo(() => {
		return formatHtml(locale, location, html, hygraphMediaDomain);
	}, [locale, location, html, hygraphMediaDomain]);

	const [gameHtml, setGameHtml] = useState<string | null>(null);

	useEffect(() => {
		if (!options?.gameMode || !baseHtml) {
			setGameHtml(null);
			return;
		}

		let cancelled = false;
		void import("./CosmosRichText.game")
			// Catch import failure only — not chained after .then — so a bug inside
			// cleanForGame still surfaces as an error instead of being swallowed here.
			.catch((err: unknown) => {
				// A chunk that never arrives (stale client after a deploy, flaky
				// network) would otherwise leave gameHtml null forever and render the
				// body permanently empty. Fall through to stripAnchors below, which
				// keeps the article readable without shipping live links.
				console.error("Failed to load in-game HTML cleanup; neutralising links inline instead", err);
				return null;
			})
			.then((mod) => {
				if (cancelled) return;
				setGameHtml(mod ? mod.cleanForGame(location, baseHtml) : stripAnchors(baseHtml));
			});
		return () => {
			cancelled = true;
		};
	}, [options?.gameMode, baseHtml, location]);

	if (options?.gameMode) {
		return gameHtml ?? "";
	}
	return baseHtml;
};
